<div
  class="relative overflow-x-hidden"
  #plotContainer
  >
  <plotly-plot
    #lisGeneratorPlot
    [useResizeHandler]="false"
    appSafeResizeHandler
    [style]="{position: 'relative', width: '100%', height: '100%'}"
    [data]="[traceForPlotly]"
    [layout]="layout"
    [config]="plotlyConfig"
    >
  </plotly-plot>

  @if (profileStatus.hasError) {
    <div
      class="invalid-segments-overlay absolute z-[1] w-full h-full top-0 left-0 backdrop-blur-[3px]"
      >
      <div class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white">
        Please ensure to enter valid values to create a preview.
      </div>
    </div>
  }
</div>

<div>
  <table>
    <thead>
      <th class="sequence-number-column w-[57px]">Seq.</th>
      <th class="action-column w-28">Action</th>
      <th class="type-column w-[202px]">Type</th>
      <th>Duration (Days Hours Minutes)</th>
      <th
        class="volume-column"
        [class.column-error]="profileStatus.maxVolumeExceeded"
        [matTooltip]="maximumVolumeExceededMessage"
        [matTooltipDisabled]="!profileStatus.maxVolumeExceeded"
        >
        Total Volume ({{ volumeUnit }})
        <small>
          @if(unknownMaxVolume === false) {
            <span>(</span>
          }
          <span [class.total-volume-exceeded-error]="profileStatus.maxVolumeExceeded">
            {{ profileStatus.totalVolume }}
          </span>
          @if(unknownMaxVolume === false) {
            <span>&nbsp;/&nbsp;{{ maxTotalVolume }})</span>
          }
        </small>
      </th>
      <th>Additonal Information</th>
    </thead>
    <tbody class="profile-generator-body">
      @for (profile of profiles; track profile; let i = $index) {
        <tr height="12px">
          <td class="sequence-cell text-center">
            {{ i + 1 }}
          </td>
          <td class="actions-cell">
            @if (readonly === false) {
              <div class="flex flex-row flex-nowrap items-center justify-center">
                <div class="re-ordering-controls flex">
                  <button
                    mat-icon-button
                    (click)="rearrangeSegment(i, 'up')"
                    [matTooltip]="'Move segment one slot up'"
                    >
                    <ion-icon name="chevron-up-outline"></ion-icon>
                  </button>
                  <button
                    mat-icon-button
                    (click)="rearrangeSegment(i, 'down')"
                    [matTooltip]="'Move segment one slot down'"
                    >
                    <ion-icon name="chevron-down-outline"></ion-icon>
                  </button>
                </div>
                <button
                  mat-icon-button
                  (click)="removeSegment(i)"
                  [matTooltip]="'Remove segment'"
                  >
                  <ion-icon name="close-circle-outline"></ion-icon>
                </button>
              </div>
            }
          </td>

          @if(readonly) {
            <td class="type-cell">
              <span>{{ profile.type }}</span>
            </td>
          } @else {
            <td
              [class.column-error]="profileStatus.segmentErrors[i]?.noTypeSelected"
              [matTooltip]="noTypeSelectedMessage"
              [matTooltipDisabled]="!profileStatus.segmentErrors[i]?.noTypeSelected"
            >
              <mat-form-field subscriptSizing="dynamic" class="type-selection-control w-[170px] text-[0.6875rem]">
                <mat-select
                  (selectionChange)="valueSelection($event, i)"
                  [(ngModel)]="profile.type"
                >
                  @for (item of valueType | keyvalue; track item) {
                    <mat-option
                      [value]="item?.value"
                      >
                      {{ item?.value }}
                    </mat-option>
                  }
                </mat-select>
              </mat-form-field>
            </td>
          }
          <td
            [class.column-error]="profileStatus.segmentErrors[i]?.maxVolumeFlowExceed"
            [matTooltip]="'The maximum volume flow rate exceeds 1000 µl/min'"
            [matTooltipDisabled]="!profileStatus.segmentErrors[i]?.maxVolumeFlowExceed"
            >
            <div class="time-selection-cell flex flex-row flex-nowrap items-center gap-1">
              <mat-form-field #formfieldDay class="compact !w-[55px] text-base">
                <input matInput id="day-input{{i}}"
                  [disabled]="readonly === true || (profiles[i].type | pureFnCall:isDisabledInputField:fieldType.day)"
                  class="max-w-[50px]"
                  [(ngModel)]="profile.duration.day"
                  (input)="trackChange('day', profile.duration.day, i, formfieldDay)"
                  type="number"
                  min="0" />
              </mat-form-field>
              <mat-form-field #formfieldHour class="compact !w-[55px] text-base">
                <input matInput id="hour-input{{i}}"
                  [disabled]="readonly === true || (profiles[i].type | pureFnCall:isDisabledInputField:fieldType.hour)"
                  class="max-w-[50px]"
                  [(ngModel)]="profile.duration.hour"
                  (input)="trackChange('hour', profile.duration.hour, i, formfieldHour)"
                  type="number"
                  min="0" />
              </mat-form-field>
              <mat-form-field #formfieldMinute class="compact !w-[55px] text-base">
                <input matInput id="minute-input{{i}}"
                  [disabled]="readonly === true || (profiles[i].type | pureFnCall:isDisabledInputField:fieldType.minute)"
                  class="max-w-[50px]"
                  [(ngModel)]="profile.duration.minute"
                  (input)="trackChange('minute', profile.duration.minute, i, formfieldMinute)"
                  type="number"
                  min="0" />
              </mat-form-field>
            </div>
          </td>
          <td
            [class.column-error]="profileStatus.maxVolumeExceeded || profileStatus.segmentErrors[i]?.maxVolumeFlowExceed || profileStatus.segmentErrors[i]?.minVolumeFlowNotReached"
            [matTooltip]="getSegmentTooltip(i)"
            [matTooltipDisabled]="!profileStatus.maxVolumeExceeded && !profileStatus.segmentErrors[i]?.maxVolumeFlowExceed && !profileStatus.segmentErrors[i]?.minVolumeFlowNotReached"
            >
            <mat-form-field class="compact">
              <input matInput id="volume-input{{i}} text-base"
                type="number"
                [disabled]="readonly === true || (profiles[i].type | pureFnCall:isDisabledInputField:fieldType.volume)"
                [(ngModel)]="profile.volume"
                (input)="trackChange('volume', profile.volume, i)"
                class="volume-input w-full"
                />
            </mat-form-field>
          </td>
          <td
            [class.column-error]="profileStatus.segmentErrors[i]?.minVolumeFlowPerShot || profileStatus.segmentErrors[i]?.additionalsInvalid"
            [matTooltip]="profileStatus.segmentErrors[i]?.minVolumeFlowPerShot
              ? getSegmentTooltip(i)
              : (profile.type === 'Multi-Shot'
                ? 'Enter the number of shots to dispense the defined amount of liquid. Example: if you enter 10000 µl and 5 repetitions, it will feed 2000 µl 5 times.'
                : (profile.type === 'Exponential'
                  ? 'Enter the growth rate of the feeding curve'
                  : ''))"
            >
            @if (profile.type === 'Multi-Shot') {
            <mat-form-field class="compact text-base">
              <input matInput
                [disabled]="readonly === true || (profiles[i].type | pureFnCall:isDisabledInputField:fieldType.repetitions)"
                class="additional-information-input{{i}}"
                type="number"
                [(ngModel)]="profile.additionalInformation!.repetitions"
                placeholder="Repetitions"
                (input)="trackChange('repetitions', profile.additionalInformation!.repetitions, i)"
                min="2"
                />
            </mat-form-field>
            }
            @if (profile.type === 'Exponential') {
            <mat-form-field class="compact text-base">
              <input matInput [disabled]="readonly === true || (profiles[i].type | pureFnCall:isDisabledInputField:fieldType.growthRate)"
                class="additional-information-input{{i}}"
                type="number"
                [(ngModel)]="profile.additionalInformation!.growthRate"
                placeholder="Growth Rate"
                (input)="trackChange('growthRate', profile.additionalInformation!.growthRate, i)"
                step="0.01"
                />
                <span matSuffix>h⁻¹</span>
            </mat-form-field>
            }
          </td>
        </tr>
      }
    </tbody>
  </table>
</div>
@if (loadingData) {
  <app-loading-overlay [targetElement]="plotContainer">
    <app-spinner size="lg"></app-spinner>
  </app-loading-overlay>
}
